import {
  Timestamp,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';

/**
 * Os schemas em @etiquetachef/types declaram campos de tempo como
 * `z.number()` (millis epoch), mas o SDK Firestore Web retorna campos
 * gravados via `FieldValue.serverTimestamp()` (Functions/Admin SDK)
 * como objetos `Timestamp`. Sem conversão, `safeParse` recusa o doc
 * inteiro com `expected number, received object`. Esta função
 * recursivamente troca cada Timestamp pelos seus `toMillis()` antes
 * do parse.
 *
 * B-24: copiado de `apps/app/src/lib/firestoreNormalize.ts` para
 * padronizar os listeners do admin (firestoreTenants tinha conversão
 * inline limitada a `createdAt`/`statusChangedAt`; com o helper
 * centralizado, qualquer Timestamp aninhado é normalizado).
 *
 * Origem: sem issue — commit e1197ff77, 2026-04-26; helper central em
 * 2a4a9a93e, 2026-05-08 (B-24).
 *
 * Usado por: `snapshotData` (abaixo) — chame aquele, não este, nos listeners.
 */
export function normalizeFirestore(value: unknown): unknown {
  if (value instanceof Timestamp) {
    return value.toMillis();
  }
  if (Array.isArray(value)) {
    return value.map(normalizeFirestore);
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = normalizeFirestore(v);
    }
    return out;
  }
  return value;
}

// `serverTimestamps: 'estimate'` faz o SDK preencher um Timestamp local
// para writes pendentes (serverTimestamp() ainda não confirmado pelo
// servidor) em vez de devolver `null` no eco otimista do onSnapshot —
// sem isso o Zod recusa o doc inteiro logo após um write client-side.
/**
 * `snap.data()` com server timestamps estimados e todo `Timestamp` em millis.
 *
 * Origem: sem issue — commit 2a4a9a93e, 2026-05-08 (B-24, normalize do admin).
 *
 * Usado por: `lib/firestoreTenants.ts`, `lib/firestoreTenantDetail.ts`,
 * `lib/firestoreMembers.ts`, `lib/firestorePrinters.ts`,
 * `lib/firestoreProducts.ts`, `lib/firestoreResponsibles.ts`,
 * `lib/firestoreLabelTemplates.ts`, `lib/firestoreProfiles.ts`.
 *
 * Dicas: todo listener novo que faz `safeParse` de doc com campo de tempo
 * deve passar por aqui — `d.data()` cru reprova o doc inteiro com
 * `expected number, received object`.
 */
export function snapshotData(
  snap: DocumentSnapshot<unknown> | QueryDocumentSnapshot<unknown>,
): Record<string, unknown> {
  const raw = snap.data({ serverTimestamps: 'estimate' });
  return normalizeFirestore(raw) as Record<string, unknown>;
}
